import {
  FilterKeys,
  FilterSection,
  GetSignUpsListQueryFilters,
  SIGN_UPS_FILTER_KEYS,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  DateRangeDropdown,
  EventsParticipationOptions,
  ExpandedSelectFilter,
  FilterDropdown,
  FiltersContainer,
  FiltersSidebarMethods,
  getParticipantLabel,
  getSignUpsStatusLabel,
  getSignUpsTypeLabel,
  PersonalFiltersDropdown,
  pickOnlyParamsFromFilterKeys,
  SignUpsStatusOptions,
  SignUpsTypeOptions,
} from '@schooly/components/filters';
import { TagSelect } from '@schooly/style';
import { FC, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import useSchoolYears from '../../hooks/useSchoolYears';
import { SignUpsFiltersSidebar } from './SignUpsFiltersSidebar';
import { useSignUpsFilters } from './useSignUpsFilters';

type SignUpsFiltersProps = {
  schoolId: string;
  filters: GetSignUpsListQueryFilters;
  defaultFilters: GetSignUpsListQueryFilters;
  defaultUserFilters: GetSignUpsListQueryFilters;
  onSetFilters: (v: GetSignUpsListQueryFilters) => void;
  notActualInitialDate?: boolean;
};

export const SignUpsFilters: FC<SignUpsFiltersProps> = ({
  defaultFilters,
  defaultUserFilters,
  onSetFilters,
  filters,
  schoolId,
  notActualInitialDate,
}) => {
  const sidebar = useRef<FiltersSidebarMethods | null>(null);
  const personalFiltersDropdown = useRef<PersonalFiltersDropdown | null>(null);
  const { currentStaff } = useAuth();
  const { formatMessage } = useIntl();
  const { defaultValidity } = useSchoolYears();

  const {
    labels,
    filtersDate,
    filtersSignUpStatus,
    filtersSignUpType,
    filtersInviteType,
    onSetDate,
    onSetSignUpStatus,
    onClearSignUpStatus,
    onSetSignUpType,
    onClearSignUpType,
    onSetInviteType,
    onClearInviteType,
  } = useSignUpsFilters({ filters, onSetFilters });

  const handleSaveFilter = useCallback(() => {
    personalFiltersDropdown.current?.saveFilter();
  }, []);

  return (
    <FiltersContainer onOpenFilters={() => sidebar.current?.open()}>
      <PersonalFiltersDropdown
        defaultSchoolYear={defaultValidity}
        ref={personalFiltersDropdown}
        currentUser={currentStaff}
        relationId={currentStaff?.relation_id || ''}
        onSaveFilter={handleSaveFilter}
        schoolId={schoolId}
        section={FilterSection.SignUps}
        filters={filters}
        onSetFilters={(v) => {
          onSetFilters(pickOnlyParamsFromFilterKeys(SIGN_UPS_FILTER_KEYS, v));
        }}
      />
      {filtersDate && (
        <DateRangeDropdown
          schoolId={schoolId}
          date={filtersDate}
          onSetDate={onSetDate}
          openLabel={labels[FilterKeys.Date]}
          defaultSchoolYear={defaultValidity}
          notActualInitialDate={notActualInitialDate}
          schoolYearFromToday
        />
      )}

      {!!filtersSignUpStatus?.length && (
        <FilterDropdown
          onClear={onClearSignUpStatus}
          label={labels[FilterKeys.SignUpStatus]}
          tags={(open) =>
            filtersSignUpStatus.map((status) => (
              <TagSelect
                key={status}
                sx={{ maxWidth: 200 }}
                onClick={open}
                label={formatMessage({ id: getSignUpsStatusLabel(status) })}
              />
            ))
          }
        >
          {(onClose) => (
            <ExpandedSelectFilter
              selected={filtersSignUpStatus}
              onApply={onSetSignUpStatus}
              onClose={onClose}
              onClear={onClearSignUpStatus}
              options={SignUpsStatusOptions}
              getLabel={(s) => formatMessage({ id: getSignUpsStatusLabel(s) })}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersSignUpType?.length && (
        <FilterDropdown
          onClear={onClearSignUpType}
          label={labels[FilterKeys.SignUpType]}
          tags={(open) =>
            filtersSignUpType.map((type) => (
              <TagSelect
                key={type}
                sx={{ maxWidth: 200 }}
                onClick={open}
                label={formatMessage({ id: getSignUpsTypeLabel(type) })}
              />
            ))
          }
        >
          {(onClose) => (
            <ExpandedSelectFilter
              selected={filtersSignUpType}
              onApply={onSetSignUpType}
              onClose={onClose}
              onClear={onClearSignUpType}
              options={SignUpsTypeOptions}
              getLabel={(type) => formatMessage({ id: getSignUpsTypeLabel(type) })}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersInviteType?.length && (
        <FilterDropdown
          onClear={onClearInviteType}
          label={labels[FilterKeys.InviteType]}
          tags={(open) =>
            filtersInviteType.map((inviteType) => (
              <TagSelect
                key={inviteType}
                sx={{ maxWidth: 200 }}
                onClick={open}
                label={formatMessage({ id: getParticipantLabel(inviteType) })}
              />
            ))
          }
        >
          {(onClose) => (
            <ExpandedSelectFilter
              selected={filtersInviteType}
              onApply={onSetInviteType}
              onClose={onClose}
              onClear={onClearInviteType}
              options={EventsParticipationOptions}
              getLabel={(i) => formatMessage({ id: getParticipantLabel(i) })}
            />
          )}
        </FilterDropdown>
      )}

      <SignUpsFiltersSidebar
        ref={sidebar}
        defaultFilters={defaultFilters}
        filters={filters}
        onApply={onSetFilters}
        schoolId={schoolId}
        defaultSchoolYear={defaultValidity}
      />
    </FiltersContainer>
  );
};
